/**
 * Client-side Audio Analyzer using Web Audio API
 * Generates waveforms and detects BPM without server processing
 * Similar to professional DJ apps like transitions.dj
 */

/**
 * Analyze audio file and generate waveform data
 * @param {string} audioUrl - URL of the audio file
 * @param {number} samples - Number of waveform samples (default 200)
 * @returns {Promise<{waveform: number[], duration: number, bpm: number|null}>}
 */
export async function analyzeAudio(audioUrl, samples = 200) {
  return new Promise(async (resolve, reject) => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      // Fetch audio file
      const response = await fetch(audioUrl, { credentials: 'include' });
      if (!response.ok) {
        throw new Error(`Failed to fetch audio: ${response.status}`);
      }
      
      const arrayBuffer = await response.arrayBuffer();
      
      // Decode audio data
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      
      const duration = audioBuffer.duration;
      const channelData = audioBuffer.getChannelData(0); // Get first channel
      
      // Generate waveform
      const waveform = generateWaveform(channelData, samples);
      
      // Detect BPM (basic tempo detection)
      const bpm = detectBPM(channelData, audioBuffer.sampleRate);
      
      audioContext.close();
      
      resolve({
        waveform,
        duration: Math.round(duration),
        bpm: bpm ? Math.round(bpm * 10) / 10 : null,
        sampleRate: audioBuffer.sampleRate
      });
    } catch (error) {
      console.error('Audio analysis error:', error);
      reject(error);
    }
  });
}

/**
 * Generate waveform data from audio samples
 * @param {Float32Array} channelData - Audio channel data
 * @param {number} samples - Number of output samples
 * @returns {number[]} - Normalized waveform values (0-1)
 */
function generateWaveform(channelData, samples) {
  const blockSize = Math.floor(channelData.length / samples);
  const waveform = [];
  
  for (let i = 0; i < samples; i++) {
    const start = i * blockSize;
    const end = start + blockSize;
    
    let sum = 0;
    let max = 0;
    
    for (let j = start; j < end && j < channelData.length; j++) {
      const abs = Math.abs(channelData[j]);
      sum += abs;
      if (abs > max) max = abs;
    }
    
    // Use RMS for smoother waveform
    const rms = sum / blockSize;
    waveform.push(Math.min(1, rms * 3)); // Normalize and amplify
  }
  
  return waveform;
}

/**
 * Basic BPM detection using peak detection
 * @param {Float32Array} channelData - Audio channel data
 * @param {number} sampleRate - Audio sample rate
 * @returns {number|null} - Detected BPM or null
 */
function detectBPM(channelData, sampleRate) {
  try {
    // Use only first 30 seconds for faster analysis
    const analysisLength = Math.min(channelData.length, sampleRate * 30);
    const samples = new Float32Array(analysisLength);
    for (let i = 0; i < analysisLength; i++) {
      samples[i] = channelData[i];
    }
    
    // Simple energy-based beat detection
    const windowSize = Math.floor(sampleRate * 0.01); // 10ms windows
    const energies = [];
    
    for (let i = 0; i < samples.length - windowSize; i += windowSize) {
      let energy = 0;
      for (let j = 0; j < windowSize; j++) {
        energy += samples[i + j] * samples[i + j];
      }
      energies.push(energy / windowSize);
    }
    
    // Find peaks (beats)
    const threshold = energies.reduce((a, b) => a + b, 0) / energies.length * 1.5;
    const peaks = [];
    
    for (let i = 1; i < energies.length - 1; i++) {
      if (energies[i] > threshold && 
          energies[i] > energies[i - 1] && 
          energies[i] > energies[i + 1]) {
        peaks.push(i);
      }
    }
    
    if (peaks.length < 4) return null;
    
    // Calculate average interval between peaks
    const intervals = [];
    for (let i = 1; i < Math.min(peaks.length, 50); i++) {
      intervals.push(peaks[i] - peaks[i - 1]);
    }
    
    const avgInterval = intervals.reduce((a, b) => a + b, 0) / intervals.length;
    const secondsPerBeat = (avgInterval * windowSize) / sampleRate;
    const bpm = 60 / secondsPerBeat;
    
    // Validate BPM range (most music is 60-200 BPM)
    if (bpm >= 60 && bpm <= 200) {
      return bpm;
    } else if (bpm >= 30 && bpm < 60) {
      return bpm * 2; // Double if too slow
    } else if (bpm > 200 && bpm <= 400) {
      return bpm / 2; // Halve if too fast
    }
    
    return null;
  } catch (error) {
    console.error('BPM detection error:', error);
    return null;
  }
}

/**
 * Generate frequency bands for visualization
 * @param {Float32Array} channelData - Audio channel data
 * @param {number} samples - Number of output samples
 * @returns {{bass: number[], mid: number[], high: number[]}}
 */
export function generateFrequencyBands(channelData, samples) {
  // Simplified frequency band generation
  // For full frequency analysis, use FFT
  const blockSize = Math.floor(channelData.length / samples);
  const bass = [];
  const mid = [];
  const high = [];
  
  for (let i = 0; i < samples; i++) {
    const start = i * blockSize;
    const end = start + blockSize;
    
    let lowSum = 0, midSum = 0, highSum = 0;
    
    for (let j = start; j < end && j < channelData.length; j++) {
      const val = Math.abs(channelData[j]);
      // Simulate frequency bands based on position (simplified)
      lowSum += val * 0.6;
      midSum += val * 0.3;
      highSum += val * 0.1;
    }
    
    bass.push(Math.min(1, (lowSum / blockSize) * 4));
    mid.push(Math.min(1, (midSum / blockSize) * 4));
    high.push(Math.min(1, (highSum / blockSize) * 4));
  }
  
  return { bass, mid, high };
}

/**
 * Quick audio info (duration only, no full analysis)
 * @param {string} audioUrl - URL of the audio file
 * @returns {Promise<{duration: number}>}
 */
export async function getAudioDuration(audioUrl) {
  return new Promise((resolve, reject) => {
    const audio = new Audio();
    audio.preload = 'metadata';
    
    audio.onloadedmetadata = () => {
      resolve({ duration: Math.round(audio.duration) });
    };
    
    audio.onerror = () => {
      reject(new Error('Failed to load audio metadata'));
    };
    
    audio.src = audioUrl;
  });
}
